import { useState } from 'react';
import { Paperclip, PenLine, Send } from 'lucide-react';
import Avatar from './Avatar';
import Button from './Button';
import { formatTimecode } from './Timecode';

const GUEST_KEY = 'hf-guest';
const readGuest = () => { try { return JSON.parse(localStorage.getItem(GUEST_KEY)) || null; } catch { return null; } };

// §2.6 / §3.3 Composer, pinned to the bottom of the comments column. The timecode chip sits inside the field.
// identity: { name, initials } for a signed-in viewer (shown as a line, never asked). identity = null → an
// anonymous share-link viewer is asked name + email once, on first post, then remembered on this device.
// onTypingStart pauses the player (type-to-pause). onPost({ text, at, name?, email? }) may return a promise.
// HD-8: submitOnEnter (Enter sends, Shift+Enter is a new line), textareaRef (a page shortcut can focus the field),
// note (replaces the hint line), testId / sendTestId (data-testid on the form and on Send, for parity probes).
export default function Composer({ identity, time, format = 'mss', fps, onPost, onTypingStart, onAttach, onDraw, placeholder = 'Leave a comment at this frame',
  submitOnEnter = false, textareaRef, note, testId, sendTestId }) {
  const [text, setText] = useState('');
  const [guest, setGuest] = useState(readGuest);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const asking = !identity && !guest;
  const submit = async e => {
    e.preventDefault();
    if (!text.trim()) { setError('Write the comment first.'); return; }
    let who = identity || guest;
    if (asking) {
      if (!name.trim() || !/^\S+@\S+\.\S+$/.test(email)) { setError('Add your name and a valid email so the team can reply.'); return; }
      who = { name: name.trim(), email: email.trim() };
    }
    setBusy(true); setError('');
    try {
      await onPost?.({ text: text.trim(), at: time, name: who?.name, email: who?.email });
      if (asking) { try { localStorage.setItem(GUEST_KEY, JSON.stringify(who)); } catch { /* private mode: ask again next time */ } setGuest(who); }
      setText('');
    } catch (err) { setError(err?.message || 'The comment did not post. Check the connection and send it again.'); }
    finally { setBusy(false); }
  };
  return (
    <form className="ui ui-composer" onSubmit={submit} aria-label="Add a comment" data-testid={testId}>
      {identity
        ? <span className="ui-composer__who"><Avatar name={identity.name} initials={identity.initials} />{identity.name}</span>
        : guest
          ? <span className="ui-composer__who"><Avatar name={guest.name} />{guest.name}</span>
          : (
            <div className="ui-composer__id">
              <label className="ui-field"><span className="ui-field__label">Your name</span><input className="ui-input" value={name} onChange={e => setName(e.target.value)} placeholder="Your name" autoComplete="name" /></label>
              <label className="ui-field"><span className="ui-field__label">Email</span><input className="ui-input" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="email@example.com" autoComplete="email" /></label>
            </div>
          )}
      <div className="ui-composer__box">
        <span className="ui-cc__chip" aria-label={`At ${formatTimecode(time, format, fps)}`}>{formatTimecode(time, format, fps)}</span>
        <textarea ref={textareaRef} className="ui-composer__text" value={text} placeholder={placeholder} aria-label="Comment"
          onChange={e => { if (!text && e.target.value) onTypingStart?.(); setText(e.target.value); }} rows={2}
          onKeyDown={submitOnEnter ? e => { if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) { e.preventDefault(); e.currentTarget.form.requestSubmit(); } } : undefined} />
      </div>
      {error && <span className="ui-field__error" role="alert">{error}</span>}
      <div className="ui-composer__tools">
        {onAttach && <Button variant="quiet" iconOnly icon={Paperclip} label="Attach a file" onClick={onAttach} />}
        {onDraw && <Button variant="quiet" iconOnly icon={PenLine} label="Draw on the frame" onClick={onDraw} />}
        <span className="ui-composer__note">{note || (asking ? 'Asked once on this device.' : 'Typing pauses the video.')}</span>
        <Button type="submit" variant="primary" icon={Send} loading={busy} data-testid={sendTestId}>Send</Button>
      </div>
    </form>
  );
}
